import { useEffect, useMemo, useState } from "react";
import DataTable from "../../components/ui/DataTable";
import Input from "../../components/ui/InputForm";
import Button from "../../components/ui/Button";
import { useAuth } from "../../context/AuthContext";

const columns = [
  { accessorKey: "name", header: "Gemeente", meta: { width: "45%" } },
  {
    accessorKey: "region",
    header: "Regio",
    meta: { width: "35%" },
    cell: (info) => info.getValue() || "—",
  },
  {
    accessorKey: "user_count",
    header: "Aantal gebruikers",
    meta: { width: "20%" },
    cell: (info) => {
      const value = Number(info.getValue() ?? 0);
      return value.toLocaleString("nl-NL");
    },
  },
];

export default function OrganisatiesTab() {
  const { tokens, isVerifying, authorizedFetch } = useAuth();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isVerifying || !tokens?.access) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);

    async function fetchOrganisations() {
      try {
        const response = await authorizedFetch("/api/organisaties/");
        const payload = await response.json().catch(() => []);
        if (!response.ok) {
          const detail = payload?.detail || "Kon organisaties niet laden.";
          throw new Error(detail);
        }
        if (!ignore) {
          setOrganisations(Array.isArray(payload) ? payload : []);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "Kon organisaties niet laden.");
          setOrganisations([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    fetchOrganisations();

    return () => {
      ignore = true;
    };
  }, [tokens?.access, isVerifying, authorizedFetch]);

  const filteredOrganisations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return organisations;
    }
    return organisations.filter((org) => {
      const name = org.name?.toLowerCase() || "";
      const region = org.region?.toLowerCase() || "";
      return name.includes(term) || region.includes(term);
    });
  }, [organisations, search]);

  const handleExport = () => {
    if (!filteredOrganisations.length) {
      return;
    }

    const header = ["Gemeente", "Regio", "Aantal gebruikers"];
    const rows = filteredOrganisations.map((org) => [
      org.name || "",
      org.region || "",
      Number(org.user_count ?? 0),
    ]);

    const csv = [header, ...rows]
      .map((line) =>
        line
          .map((value) => {
            const safe = `${value ?? ""}`.replace(/"/g, '""');
            return `"${safe}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `organisaties_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resolvedDescription = error
    ? error
    : loading && !organisations.length
    ? "Organisaties worden geladen…"
    : organisations.length
    ? "Overzicht van gemeenten met gekoppelde gebruikers."
    : "Er zijn nog geen gemeenten geregistreerd.";

  return (
    <DataTable
      title="Organisaties"
      label={`${filteredOrganisations.length} organisaties`}
      description={resolvedDescription}
      data={filteredOrganisations}
      columns={columns}
      toolbarRight={
        <>
          <div className="filter-item search-item">
            <Input
              name="search"
              type="text"
              placeholder="Zoek op gemeente of regio"
              leftIcon="/icons/glass.png"
              wrapperClassName="filter-input-wrapper m0"
              inputClassName="filter-input"
              noLabel
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            icon="/icons/download-cloud-02.png"
            iconPosition="left"
            onClick={handleExport}
            disabled={!filteredOrganisations.length}
          >
            Download CSV
          </Button>
        </>
      }
    />
  );
}
